"use client";
import {
  Backdrop,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { CloseIcon } from "@/icons";
import { memo, useMemo } from "react";
import { AppModalProps, SizeModalEnum } from "@/models";
import clsx from "clsx";
import { cn } from "@/utils/common.utils";

const AppModal = ({
  sx,
  isShowCloseIcon = true,
  header,
  content,
  footer,
  onClose: closeModal,
  size = SizeModalEnum.Md,
  fullHeight = false,
  modalHeaderProps = {},
  modalContentProps = {},
  modalFooterProps = {},
  closeIconProps = {},
  PaperProps,
  fullScreen = false,
  closeWhenClickBackdrop = false,
  ...otherProps
}: AppModalProps) => {
  const {
    className: contentClassName,
    sx: contentSx,
    ...otherModalContentProps
  } = modalContentProps;
  const {
    className: headerClassName = "text-center",
    sx: headerSx,
    ...otherModalHeaderProps
  } = modalHeaderProps;
  const { sx: footerSx, ...otherDialogFooterProps } = modalFooterProps;
  const {
    sx: closeIconSx,
    customIcon,
    ...otherCloseIconProps
  } = closeIconProps;
  const maxWidth = useMemo(() => {
    if (size === SizeModalEnum.Sm) {
      return "500px";
    }
    if (size === SizeModalEnum.Md) {
      return "690px";
    }
    if (size === SizeModalEnum.Lg) {
      return "1240px";
    }
    if (size === SizeModalEnum.Xl) {
      return "1340px";
    }
    if (size === SizeModalEnum.Full) {
      return "100%";
    }
    return "690px";
  }, [size]);
  const { sx: paperSx, ...otherPaperProps } = { ...PaperProps };
  const handleClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason === "backdropClick" && !closeWhenClickBackdrop) {
    } else {
      closeModal();
    }
  };
  return (
    <Dialog
      fullScreen={fullScreen}
      scroll="paper"
      sx={{
        ...sx,
      }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: "rgba(36, 54, 72, 0.95)",
          },
        },
      }}
      disableEnforceFocus
      onClose={handleClose}
      PaperProps={{
        sx: {
          maxWidth: maxWidth,
          "&&": {
            transition: "max-width 300ms",
          },
          width: "100%",
          margin: "20px",
          borderRadius: "12px",
          maxHeight: "calc(100vh - 40px)",
          height: fullHeight ? "100vh" : "auto",
          display: "flex",
          flexDirection: "column",
          "& .MuiDialogContent-root + .MuiDialogActions-root": {
            padding: "20px 24px",
            margin: 0,
            borderTop: "1.5px solid",
            borderColor: "border.main",
          },
          ".MuiDialogTitle-root+.MuiDialogContent-root": {
            paddingTop: "24px",
          },
          ...paperSx,
        },
        ...otherPaperProps,
      }}
      {...otherProps}
    >
      {isShowCloseIcon && (
        <IconButton
          sx={{
            position: "absolute",
            top: "8px",
            right: "16px",
            fontSize: 24,
            p: 1,
            borderRadius: "50%",
            color: "#1c1e21",
            backgroundColor: "#e4e6eb",
            ...closeIconSx,

            ":hover": {
              backgroundColor: "#d8dadf",
            },
          }}
          onClick={closeModal}
          {...otherCloseIconProps}
        >
          {customIcon || <CloseIcon />}
        </IconButton>
      )}

      {header && (
        <DialogTitle
          className={clsx("h3 font-bold", headerClassName)}
          sx={{
            borderBottom: "1px solid #DBDBDB",
            padding: "12px 24px",
            flex: "0 0 auto",
            ...headerSx,
          }}
          {...otherModalHeaderProps}
        >
          {header}
        </DialogTitle>
      )}

      {content && (
        <DialogContent
          sx={{
            p: "24px",
            pt: "24px",
            flex: "1 1 100%",
            ...contentSx,
          }}
          className={clsx("custom-scrollbar h5", contentClassName)}
          {...otherModalContentProps}
        >
          {content}
        </DialogContent>
      )}

      {footer && (
        <DialogActions
          sx={{
            p: "0px 24px 0 24px",
            justifyContent: "flex-end",
            margin: "24px 0",
            gap: "8px",
            flex: "0 0 auto",
            "& >:not(style)~:not(style)": {
              margin: "unset",
            },
            ...footerSx,
          }}
          {...otherDialogFooterProps}
        >
          {footer}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default memo(AppModal);
